import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { AssetTimeline, AssetTimelineParams } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { AssetTimelineInfo } from '../entities/asset-timeline-info.entity';
import { DerivativeData, DerivativeDataVisibility } from '../entities/derivative-data.entity';

@Injectable({ providedIn: 'root' })
export class InstrumentDetailInfrastructure {
  readonly #http = inject(HttpClient);
  readonly #authFacade = inject(AuthStateFacade);
  readonly #derivativeDataVisibilityKey = 'DerivativeBaseAndKeyDataVisibility';
  readonly #assetTimelineUrl = `${environment.apihost + environment.tradingServiceUrl}/assettimeline`;

  public loadAssetTimeline(assetTimelineParams: AssetTimelineParams): Observable<AssetTimeline> {
    if (!assetTimelineParams.isin || !this.#authFacade.customerId) {
      return of(null);
    }

    const params = {
      customerId: this.#authFacade.customerId,
      page: assetTimelineParams.page ?? '0',
      size: assetTimelineParams.size,
    };
    return this.#http.get<AssetTimeline>(`${this.#assetTimelineUrl}/${assetTimelineParams.isin}`, { params });
  }

  public loadAssetTimelineInfo(isin: string): Observable<AssetTimelineInfo> {
    return isin && this.#authFacade.customerId
      ? this.#http.get<AssetTimelineInfo>(`${this.#assetTimelineUrl}/${isin}/info`, {
          params: { customerId: this.#authFacade.customerId },
        })
      : of({ exists: false, firstItem: null });
  }

  public checkDerivativeDataVisibility(): Observable<DerivativeDataVisibility> {
    return of(this.#getCachedDerivativeDataVisibility());
  }

  public loadDerivativeData(isin: string): Observable<DerivativeData> {
    return this.#http.get<DerivativeData>(`${environment.apihost + environment.instrumentServiceUrl}/derivative/${isin}`);
  }

  public toggleDerivativeBaseDataVisibility(): Observable<DerivativeDataVisibility> {
    const cache = this.#getCachedDerivativeDataVisibility();
    return of(
      !cache
        ? { isBaseDataVisible: false, isKeyDataVisible: true, isPayoutProfileVisible: false }
        : { ...cache, isBaseDataVisible: !cache.isBaseDataVisible },
    );
  }

  public toggleDerivativeKeyDataVisibility(): Observable<DerivativeDataVisibility> {
    const cache = this.#getCachedDerivativeDataVisibility();
    return of(
      !cache
        ? { isBaseDataVisible: true, isKeyDataVisible: false, isPayoutProfileVisible: false }
        : { ...cache, isKeyDataVisible: !cache.isKeyDataVisible },
    );
  }

  public toggleDerivativePayoutProfileVisibility(): Observable<DerivativeDataVisibility> {
    const cache = this.#getCachedDerivativeDataVisibility();
    return of(
      !cache
        ? { isBaseDataVisible: false, isKeyDataVisible: false, isPayoutProfileVisible: true }
        : { ...cache, isPayoutProfileVisible: !cache.isPayoutProfileVisible },
    );
  }

  public cacheDerivativeDataVisibility(visibility: DerivativeDataVisibility) {
    return localStorage.setItem(this.#derivativeDataVisibilityKey, JSON.stringify(visibility));
  }

  readonly #getCachedDerivativeDataVisibility = () => JSON.parse(localStorage.getItem(this.#derivativeDataVisibilityKey));
}
